import React, { useContext, useState } from 'react';
import { ShoppingCartIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { CartContext } from '../../context/CartContext';
import { BookingContext } from '../../context/BookingContext';
import AccountDropdown from '../shared/AccountDropdown';
import { isLakehouseContext } from '../../utils/restaurantContext';

// Header now contains the gray top bar with nav links to match the mockup
const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isLoggedIn, openLoginModal, user } = useContext(AuthContext);
    const location = useLocation();
    const showCartIcon = location.pathname === '/menu' || location.pathname.startsWith('/menu');
    const { count = 0 } = React.useContext(CartContext) || {};
    const isLakehouseView = isLakehouseContext(user, location.pathname);
    const homePath = isLakehouseView ? '/lakehouse' : '/';
    const menuPath = isLakehouseView ? '/menu?restaurant_id=2' : '/menu';
    const headerLogo = isLakehouseView ? '/images/casagatchalianlakehouse_logo.png' : '/images/casa-gatchalian-logo.jpg';
    const brandLabel = isLakehouseView ? 'CASA GATCHALIAN LAKEHOUSE' : 'CASA GATCHALIAN';

    // Show accommodation only if:
    // - User is not logged in (customer browsing), OR
    // - User is admin (restaurant_id is null), OR
    // - User is customer, OR
    // - User is staff for Casa Gatchalian (restaurant_id === 1)
    const showAccommodation = !isLakehouseView && (!user || 
                              user.role === 'customer' || 
                              (user.role === 'admin') ||
                              (user.role === 'staff' && user.restaurant_id === 1));

    return (
        <header className="w-full bg-[#000000] text-white shadow-lg border-b border-white/10">
            <div className="max-w-8xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4 md:py-5">
                <div className="flex items-center justify-between">
                    {/* Logo and Navigation - Left side */}
                    <div className="flex items-center gap-3 sm:gap-6 md:gap-8">
                        <Link to={homePath} className="flex items-center gap-2 sm:gap-3">
                            <div className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0 ring-1 ring-white/20">
                                <img src={headerLogo} alt={brandLabel} className="w-full h-full object-cover" />
                            </div>
                            <span className="text-white font-bebas text-xs sm:text-base md:hidden tracking-wider">{brandLabel}</span>
                            <span className="sr-only">{brandLabel} Home</span>
                        </Link>

                        {/* Desktop Navigation - Next to logo */}
                        <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
                            <Link to={homePath} className="text-white font-bebas tracking-widest text-base lg:text-xl hover:bg-white/5 hover:text-white px-3 lg:px-4 py-2 rounded transition-colors">
                                HOME
                            </Link>

                            <Link to={menuPath} className="text-white font-bebas tracking-widest text-base lg:text-xl hover:bg-white/5 hover:text-white px-3 lg:px-4 py-2 rounded transition-colors">
                                MENU
                            </Link>

                            {showAccommodation && (
                                <Link to="/accommodation" className="text-white font-bebas tracking-widest text-base lg:text-xl hover:bg-white/5 hover:text-white px-3 lg:px-4 py-2 rounded transition-colors">
                                    ACCOMMODATION
                                </Link>
                            )}
                        </nav>
                    </div>

                    {/* Right side icons */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        {showCartIcon && (
                            <Link to="/cart" aria-label="Cart" className="text-white hover:opacity-80 transition-opacity relative p-1">
                                <ShoppingCartIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                {count > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-[9px] sm:text-[10px] font-semibold text-white rounded-full w-4 h-4 flex items-center justify-center ring-1 ring-white/80">
                                        {count > 99 ? '99+' : count}
                                    </span>
                                )}
                            </Link>
                        )}

                        {/* Account/Login - Desktop */}
                        <div className="hidden md:block">
                            {isLoggedIn ? (
                                <AccountDropdown />
                            ) : (
                                <button onClick={openLoginModal} aria-label="Sign in" className="text-white hover:opacity-80 transition-opacity bg-transparent p-1">
                                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden text-white hover:opacity-80 transition-opacity p-1"
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? (
                                <XMarkIcon className="w-6 h-6" />
                            ) : (
                                <Bars3Icon className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-[#0a0a0a] border-t border-white/10">
                    <nav className="flex flex-col">
                        <Link
                            to={homePath}
                            className="text-white font-bebas tracking-widest text-base px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            HOME
                        </Link>
                        <Link
                            to={menuPath}
                            className="text-white font-bebas tracking-widest text-base px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            MENU
                        </Link>
                        {showAccommodation && (
                            <Link
                                to="/accommodation"
                                className="text-white font-bebas tracking-widest text-base px-6 py-4 hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                ACCOMMODATION
                            </Link>
                        )}
                        
                        {/* Account/Login in mobile menu */}
                        <div className="px-6 py-4 bg-black/30">
                            {isLoggedIn ? (
                                <AccountDropdown />
                            ) : (
                                <button
                                    onClick={() => {
                                        openLoginModal();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="text-white font-bebas tracking-widest text-sm hover:bg-white/5 active:bg-white/10 transition-colors w-full text-left flex items-center gap-3 py-2.5 px-3 rounded border border-white/20"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                    SIGN IN
                                </button>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;